import { useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { insertTransactionIdempotent } from '@/lib/insertTransaction';

const FREQ_DAYS = { weekly: 7, monthly: 30, quarterly: 92, yearly: 365 };
const SESSION_FLAG = '_2120_recurring_autorun_v1';

function addDaysISO(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * RecurringAutoRun, runs once per browser session for the signed-in user.
 *
 * Scans active recurring_expenses with next_due <= today and:
 *   - creates a transaction (review_needed=true, recurring=true, source='auto') for each due cycle missed
 *   - rolls next_due forward by the item's frequency
 *
 * Idempotent: uses sessionStorage to skip on subsequent route renders. The
 * "review_needed" column lets the user adjust amounts later. We also avoid
 * duplicates by checking existing recurring transactions for the same
 * (description, txn_date).
 */
export default function RecurringAutoRun() {
  const { user } = useAuth();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!user || ranRef.current) return;
    if (typeof window === 'undefined') return;

    const flagKey = `${SESSION_FLAG}_${user.id}`;
    if (sessionStorage.getItem(flagKey) === '1') {
      ranRef.current = true;
      return;
    }

    ranRef.current = true;

    (async () => {
      try {
        const today = todayISO();
        const { data: items, error } = await supabase
          .from('recurring_expenses')
          .select('*')
          .eq('active', true);
        if (error) throw error;
        if (!items || items.length === 0) {
          sessionStorage.setItem(flagKey, '1');
          return;
        }

        const due = items.filter((i) => i.next_due && i.next_due <= today);
        if (due.length === 0) {
          sessionStorage.setItem(flagKey, '1');
          return;
        }

        for (const item of due) {
          const stepDays = FREQ_DAYS[item.frequency] || 30;
          let cursor = item.next_due;
          // Step forward through any missed cycles, capped to avoid runaway
          let safety = 24;
          while (cursor && cursor <= today && safety-- > 0) {
            // Phase 59 — idempotent insert. The fingerprint includes
            // user_id + account_id + txn_date + amount + normalised
            // description, so re-running this scheduler (multiple
            // browser tabs, fast restart, network retry) is a true
            // no-op. The pre-flight SELECT we used to do here had a
            // race window between check and insert that could yield
            // dupes; the upsert removes that.
            await insertTransactionIdempotent({
              user_id: user.id,
              amount: Number(item.amount),
              type: 'expense',
              category: item.category || 'Subscriptions',
              description: `${item.name} (recurring)`,
              txn_date: cursor,
              recurring: true,
              review_needed: true,
              source: 'auto',
            });
            cursor = addDaysISO(cursor, stepDays);
          }
          // Roll next_due forward past today
          await supabase
            .from('recurring_expenses')
            .update({ next_due: cursor })
            .eq('id', item.id);
        }
      } catch (err) {
        // Silent, auto-run should never disrupt the user. Logged for devs.
        console.warn('[RecurringAutoRun] skipped:', err?.message || err);
      } finally {
        sessionStorage.setItem(flagKey, '1');
      }
    })();
  }, [user]);

  return null;
}
